import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import {
  useAttachEntityImage,
  useBatchRemoveEntityImages,
  useEntity,
  useRemoveEntityImage,
  useSetReferenceImage,
  type EntityImageDto,
} from '../../api/queries/entities';
import { useImages, useUploadImage } from '../../api/queries/media';
import { AppImage } from '../common/AppImage';
import { ImageLightbox } from '../common/ImageLightbox';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { ImageCaptionEditor } from './ImageCaptionEditor';

interface Props {
  entityId: string;
  images?: EntityImageDto[];
  onRequestGenerate?: () => void;
}

export function EntityImageGallery({
  entityId,
  images: imagesProp,
  onRequestGenerate,
}: Props) {
  const entityQuery = useEntity(imagesProp ? undefined : entityId);
  const images = imagesProp ?? entityQuery.data?.images ?? [];
  const [pickerOpen, setPickerOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const setRef = useSetReferenceImage(entityId);
  const remove = useRemoveEntityImage(entityId);
  const batchRemove = useBatchRemoveEntityImages(entityId);
  const uploadImage = useUploadImage();
  const attachImage = useAttachEntityImage(entityId);

  // Multi-select state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(images.map((img) => img.id)));
  };

  const deselectAll = () => {
    setSelected(new Set());
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const uploaded = await uploadImage.mutateAsync(file);
        await attachImage.mutateAsync({ imageId: uploaded.id });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const lightboxImages = images
    .filter((img) => img.imageUrl || img.thumbnailUrl)
    .map((img) => ({
      src: img.imageUrl ?? img.thumbnailUrl ?? '',
      alt: `Entity image`,
      label: img.isReference
        ? `Reference image${img.generatedCaption ? ` — ${img.generatedCaption}` : ''}`
        : (img.generatedCaption ?? undefined),
    }));

  const captionedCount = images.filter((i) => i.generatedCaption).length;

  if (images.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No images yet. Attach at least one to set a reference.
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          sx={{ mt: 2 }}
        >
          <Button variant="contained" onClick={() => setPickerOpen(true)}>
            Attach image
          </Button>
          <Button
            variant="outlined"
            startIcon={
              uploading ? <CircularProgress size={16} /> : <UploadIcon />
            }
            onClick={handleUploadClick}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
          {onRequestGenerate && (
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={onRequestGenerate}
            >
              AI Generate
            </Button>
          )}
        </Stack>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <ImagePicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          entityId={entityId}
        />
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {images.length} image{images.length === 1 ? '' : 's'} —{' '}
          {captionedCount > 0
            ? `${captionedCount} captioned. `
            : 'Captions appear after first training. '}
          Click the star to set reference. The reference image drives LoRA +
          consistency checks.
        </Typography>

        {selectMode ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {selected.size} selected
            </Typography>
            <Tooltip title="Select all">
              <IconButton onClick={selectAll} size="small">
                <SelectAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Deselect all">
              <IconButton onClick={deselectAll} size="small">
                <DeselectIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={selected.size === 0 || batchRemove.isPending}
              onClick={() => setBatchDeleteConfirm(true)}
              startIcon={
                batchRemove.isPending ? (
                  <CircularProgress size={14} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {batchRemove.isPending
                ? 'Removing...'
                : `Remove (${selected.size})`}
            </Button>
            <Button size="small" variant="outlined" onClick={exitSelectMode}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectMode(true)}
            >
              Select
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPickerOpen(true)}
            >
              Attach image
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                uploading ? <CircularProgress size={14} /> : <UploadIcon />
              }
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
            {onRequestGenerate && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoFixHighIcon />}
                onClick={onRequestGenerate}
              >
                AI Generate
              </Button>
            )}
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 1.25,
        }}
      >
        {images.map((img, index) => (
          <Paper
            key={img.id}
            variant="outlined"
            sx={{
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              outline: selected.has(img.id)
                ? '3px solid'
                : img.isReference
                  ? '2px solid'
                  : 'none',
              outlineColor: selected.has(img.id)
                ? 'error.main'
                : 'primary.main',
            }}
          >
            <Box sx={{ position: 'relative', aspectRatio: '1 / 1' }}>
              {selectMode && (
                <Checkbox
                  checked={selected.has(img.id)}
                  onChange={() => toggleSelect(img.id)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    zIndex: 2,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                  size="small"
                />
              )}
              <AppImage
                src={img.thumbnailUrl ?? img.imageUrl}
                alt="entity reference"
                width="100%"
                height="100%"
                objectPosition="top center"
                onClick={() => {
                  if (selectMode) {
                    toggleSelect(img.id);
                  } else {
                    setLightboxIndex(index);
                  }
                }}
                fallbackIcon={
                  <Typography variant="caption" color="text.secondary">
                    (no preview)
                  </Typography>
                }
                sx={{ borderRadius: 0, cursor: 'pointer' }}
              />
              {!selectMode && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                  }}
                >
                  <Tooltip
                    title={img.isReference ? 'Reference' : 'Set as reference'}
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={img.isReference || setRef.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRef.mutate(img.id);
                        }}
                      >
                        {img.isReference ? '★' : '☆'}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove.mutate(img.id);
                      }}
                      color="error"
                    >
                      ✕
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Box>
            {!selectMode && (
              <Box
                sx={{ p: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
              >
                <ImageCaptionEditor
                  entityId={entityId}
                  entityImageId={img.id}
                  caption={img.generatedCaption}
                />
              </Box>
            )}
          </Paper>
        ))}
      </Box>

      <ImageLightbox
        open={lightboxIndex >= 0}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(-1)}
        onIndexChange={setLightboxIndex}
      />

      <ImagePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        entityId={entityId}
      />

      {/* Batch remove confirmation */}
      <ConfirmDialog
        open={batchDeleteConfirm}
        title="Remove selected images"
        message={`Remove ${selected.size} image${selected.size === 1 ? '' : 's'} from this entity?`}
        confirmLabel={`Remove ${selected.size}`}
        confirmColor="error"
        onConfirm={async () => {
          setBatchDeleteConfirm(false);
          await batchRemove.mutateAsync(Array.from(selected));
          exitSelectMode();
        }}
        onCancel={() => setBatchDeleteConfirm(false)}
      />
    </Stack>
  );
}

function ImagePicker({
  open,
  onClose,
  entityId,
}: {
  open: boolean;
  onClose: () => void;
  entityId: string;
}) {
  const images = useImages();
  const attach = useAttachEntityImage(entityId);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Attach image</DialogTitle>
      <DialogContent>
        {images.isLoading && <Typography>Loading...</Typography>}
        {images.data && images.data.length === 0 && (
          <Alert severity="info">
            No images in your library. Upload one via /images first.
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 1,
            mt: 1,
          }}
        >
          {images.data?.map((img) => (
            <Paper
              key={img.id}
              sx={{
                aspectRatio: '1 / 1',
                overflow: 'hidden',
                cursor: 'pointer',
                ':hover': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                },
              }}
              onClick={async () => {
                await attach.mutateAsync({ imageId: img.id });
                onClose();
              }}
            >
              <AppImage
                src={img.thumbnailUrl ?? img.url}
                alt={img.fileName}
                width="100%"
                height="100%"
                objectPosition="top center"
                sx={{ borderRadius: 0 }}
              />
            </Paper>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
